import { Module } from "vuex"
import {Message} from "@/types/client";

const LOCAL_STORAGE_KEY = 'voxbet_ui_overrides'

function getInitialOverrides() {
  try {
    const str = localStorage.getItem(LOCAL_STORAGE_KEY)
    return str ? JSON.parse(str) : {}
  } catch (e) {
    return {}
  }
}

function initialState() {
  return {
    stbApiHost: "",
    stbApiIsSecure: true,
    appClientId: "",
    authServerBaseUrl: "",
    appRootUrl: "",
    staticBaseUrl: "",
    operationMode: "",
    appClientHome: "",
		messages: [] as Message[],
    authToken: "",
    themes: [] as any[],
		uiOptions: {} as UIOptions,
    uiOverrides: getInitialOverrides() as Record<string, boolean>
  }
}

export interface UIOptions {
	betSlipAsImageEnabled: boolean
	shareAttachImage: boolean
	linkToShareImage: boolean
	shareImagePreview: boolean
	speakEnabled: boolean
	insightEnabled: boolean
	autoQueryEnabled: boolean
	betSlipViewerCopyBetEnabled: false
	showAllContexts: false
}

const configModule: Module<any, any> = {
  namespaced: true,

  state: initialState,

  mutations: {
    resetConfigStore(state) {
      const initial: any = initialState()
      Object.keys(initial).forEach((key) => {
        if (key in state) {
          state[key] = initial[key]
        }
      })
    },

    setStbApiHost(state, stbApiHost) {
      state.stbApiHost = stbApiHost
    },

    setStbApiIsSecure(state, stbApiIsSecure) {
      state.stbApiIsSecure = stbApiIsSecure
    },

    setAppClientId(state, appClientId) {
      state.appClientId = appClientId
    },

    setAuthServerBaseUrl(state, authServerBaseUrl) {
      state.authServerBaseUrl = authServerBaseUrl
    },

    setAppRootUrl(state, appRootUrl) {
      state.appRootUrl = appRootUrl
    },

    setStaticBaseUrl(state, staticBaseUrl) {
      state.staticBaseUrl = staticBaseUrl
    },

    setOperationMode(state, operationMode) {
      state.operationMode = operationMode
    },

		setUIOptions(state, uiOptions) {
			state.uiOptions = uiOptions
		},

    setUIOverride(state, { key, value }: { key: string; value: boolean }) {
      if (value === state.uiOptions[key]) {
        const newOverrides = { ...state.uiOverrides }
        delete newOverrides[key]
        state.uiOverrides = newOverrides
      } else {
        state.uiOverrides = { ...state.uiOverrides, [key]: value }
      }
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.uiOverrides))
    },

    resetUIOverrides(state) {
      state.uiOverrides = {}
      localStorage.removeItem(LOCAL_STORAGE_KEY)
    },

    setAppClientHome(state, appClientHome) {
      state.appClientHome = appClientHome
    },

		setMessages(state, messages) {
			state.messages = messages
		},

    setAuthToken(state, authToken) {
			state.authToken = authToken
		},

    setThemes(state, themes) {
			state.themes = themes
		},
  },

  actions: {
    resetConfigStore({ commit }) {
      commit("resetConfigStore")
    },

    setStbApiHost({ commit }, stbApiHost) {
      commit("setStbApiHost", stbApiHost)
    },

    setStbApiIsSecure({ commit }, stbApiIsSecure) {
      commit("setStbApiIsSecure", stbApiIsSecure)
    },

    setAppClientId({ commit }, appClientId) {
      commit("setAppClientId", appClientId)
    },

    setAuthServerBaseUrl({ commit }, authServerBaseUrl) {
      commit("setAuthServerBaseUrl", authServerBaseUrl)
    },

    setAppRootUrl({ commit }, appRootUrl) {
      commit("setAppRootUrl", appRootUrl)
    },

    setStaticBaseUrl({ commit }, staticBaseUrl) {
      commit("setStaticBaseUrl", staticBaseUrl)
    },

    setOperationMode({ commit }, operationMode) {
      commit("setOperationMode", operationMode)
    },

		setUIOptions({ commit }, uiOptions) {
			commit("setUIOptions", uiOptions)
		},

    setUIOverride({ commit }, payload: { key: string; value: boolean }) {
      commit("setUIOverride", payload)
    },

    resetUIOverrides({ commit }) {
      commit("resetUIOverrides")
    },

    setAppClientHome({ commit }, appClientHome) {
      commit("setAppClientHome", appClientHome)
    },

		setMessages({ commit }, messages) {
			commit("setMessages", messages)
		},

    setAuthToken({ commit }, authToken) {
			commit("setAuthToken", authToken)
		},

    setThemes({ commit }, themes) {
			commit("setThemes", themes)
		},
  },

  getters: {
    stbApiHost(state) {
      return state.stbApiHost
    },

    stbApiIsSecure(state) {
      return state.stbApiIsSecure
    },

    appClientId(state) {
      return state.appClientId
    },

    authServerBaseUrl(state) {
      return state.authServerBaseUrl
    },

    appRootUrl(state) {
      return state.appRootUrl
    },

    staticBaseUrl(state) {
      return state.staticBaseUrl
    },

    operationMode(state) {
      return state.operationMode
    },

		uiOptions(state) {
			return { ...state.uiOptions, ...state.uiOverrides }
		},

    uiOverrides(state) {
      const overrides: Record<string, boolean> = {}
      for (const key in state.uiOverrides) {
        if (state.uiOverrides[key] !== state.uiOptions[key]) {
          overrides[key] = state.uiOverrides[key]
        }
      }
      return overrides
    },

    appClientHome(state) {
      return state.appClientHome
    },

		messages(state) {
			return state.messages
		},

    authToken(state) {
			return state.authToken
		},

    themes(state) {
			return state.themes
		},
  },
}

export default configModule
